import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  CheckCircle, 
  CreditCard, 
  Smartphone,
  Monitor,
  Wifi,
  Shield,
  Clock,
  DollarSign,
  ArrowRight,
  Phone,
  X,
  User,
  Mail,
  Building,
  GitCompare
} from 'lucide-react';
import { 
  createUserWithEmailAndPassword, 
  signInWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  onAuthStateChanged
} from 'firebase/auth';
import { doc, setDoc, collection, addDoc, getDoc, query, onSnapshot, getDocs } from 'firebase/firestore';
import { auth, db } from '../firebase';
import { getProducts, Product } from '../services/productService';
import { logger } from '../utils/logger';
import { ScrollReveal } from '../components/ScrollReveal';

const Products: React.FC = () => {
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [showQuoteModal, setShowQuoteModal] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<any>(null);
  const [hasAccount, setHasAccount] = useState<boolean | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [authError, setAuthError] = useState<string>('');
  const [isLoginMode, setIsLoginMode] = useState(false);
  const [products, setProducts] = useState<Product[]>([]);
  const [isLoadingProducts, setIsLoadingProducts] = useState(true);
  const [compareProducts, setCompareProducts] = useState<Product[]>([]);
  const [showCompareModal, setShowCompareModal] = useState(false);
  const [showLearnMoreModal, setShowLearnMoreModal] = useState(false);
  const [selectedLearnMoreProduct, setSelectedLearnMoreProduct] = useState<Product | null>(null);

  // Initialize Google Auth Provider
  const googleProvider = new GoogleAuthProvider();
  
  const [formData, setFormData] = useState({
    // Account info
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    
    // Business info
    businessName: '',
    businessAddress: '',
    city: '',
    state: '',
    zipCode: '',
    phone: '',
    
    // Business details
    businessType: '',
    monthlyVolume: '',
    currentProcessor: '',
    timeline: '',
    additionalNeeds: ''
  });

  const categories = [
    { id: 'all', name: 'All Products' },
    { id: 'pos-systems', name: 'POS Systems' },
    { id: 'mobile-solutions', name: 'Mobile Solutions' },
    { id: 'online-payments', name: 'Online Payments' },
    { id: 'handheld-terminals', name: 'Handheld Terminals' }
  ];

  const businessTypes = [
    'Retail Store',
    'Restaurant/Food Service',
    'Healthcare/Medical',
    'Professional Services',
    'E-commerce/Online',
    'Manufacturing',
    'Construction',
    'Automotive',
    'Beauty/Salon',
    'Fitness/Wellness',
    'Real Estate',
    'Education',
    'Non-profit',
    'Other'
  ];

  const timelines = [
    'Immediately',
    'Within 1 month',
    'Within 3 months',
    'Within 6 months',
    'Just exploring options'
  ];

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Function to load user data from Firestore
  const loadUserData = async (user: any) => {
    try {
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        setFormData(prev => ({
          ...prev,
          email: user.email || '',
          firstName: userData.firstName || user.displayName?.split(' ')[0] || '',
          lastName: userData.lastName || user.displayName?.split(' ').slice(1).join(' ') || '',
          phone: userData.phone || user.phoneNumber || '',
          businessName: userData.businessName || '',
          businessAddress: userData.businessAddress || '',
          city: userData.city || '',
          state: userData.state || '',
          zipCode: userData.zipCode || '',
          businessType: userData.businessType || '',
          monthlyVolume: userData.monthlyVolume || '',
          currentProcessor: userData.currentProcessor || '',
          timeline: userData.timeline || '',
          additionalNeeds: userData.additionalNeeds || ''
        }));
      } else {
        // If no saved data, just populate with basic user info
        setFormData(prev => ({
          ...prev,
          email: user.email || '',
          firstName: user.displayName?.split(' ')[0] || '',
          lastName: user.displayName?.split(' ').slice(1).join(' ') || '',
          phone: user.phoneNumber || ''
        }));
      }
    } catch (error) {
      logger.log('Could not load user data from Firestore, using basic info');
      // Fallback to basic user info
      setFormData(prev => ({
        ...prev,
        email: user.email || '',
        firstName: user.displayName?.split(' ')[0] || '',
        lastName: user.displayName?.split(' ').slice(1).join(' ') || '',
        phone: user.phoneNumber || ''
      }));
    }
  };

  // Listen for authentication state changes
  React.useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
      
      // Pre-populate form with user data if available
      if (user) {
        loadUserData(user);
      }
    });
    return () => unsubscribe();
  }, []);

  // Load products from Firestore with real-time updates
  useEffect(() => {
    logger.log('🎯 Products Page: Loading products immediately, then setting up real-time listener');
    setIsLoadingProducts(true);

    let isMounted = true;
    let unsubscribeProducts: (() => void) | null = null;

    // First, load products immediately for faster initial display
    const loadProductsImmediately = async () => {
      try {
        const productsQuery = query(collection(db, 'products'));
        const snapshot = await getDocs(productsQuery);
        
        if (!isMounted) return;
        
        const productsData = snapshot.docs.map(doc => ({ 
          id: doc.id, 
          ...doc.data() 
        } as Product));

        logger.log('📦 Loaded products immediately:', productsData.length, 'products');
        setProducts(productsData);
        setIsLoadingProducts(false);
      } catch (error) {
        logger.error('❌ Error loading products immediately:', error);
        if (isMounted) {
          setIsLoadingProducts(false);
        }
      }
    };

    // Load immediately
    loadProductsImmediately();

    // Set up real-time listener for updates (but don't block initial load)
    const productsQuery = query(collection(db, 'products'));
    unsubscribeProducts = onSnapshot(productsQuery, (snapshot) => {
      try {
        if (!isMounted) return;
        
        logger.log('🔄 Products Page: Real-time update received!');
        logger.log('📊 Products snapshot:', snapshot.docs.length, 'documents');

        const productsData = snapshot.docs.map(doc => ({ 
          id: doc.id, 
          ...doc.data() 
        } as Product));

        logger.log('📦 Updated products catalog:', productsData.length, 'products');
        setProducts(productsData);
        // Only set loading to false if we haven't loaded yet (fallback)
        setIsLoadingProducts(false);
      } catch (error) {
        logger.error('❌ Error processing products update:', error);
        if (isMounted) {
          setIsLoadingProducts(false);
        }
      }
    }, (error) => {
      logger.error('❌ Products listener error:', error);
      if (isMounted) {
        setIsLoadingProducts(false);
      }
    });

    // Cleanup listener on unmount
    return () => {
      isMounted = false;
      logger.log('🧹 Products Page: Cleaning up real-time listener');
      if (unsubscribeProducts) {
        unsubscribeProducts();
      }
    };
  }, []);

  const handleGetQuote = (product: any) => {
    navigate(`/contact?product=${encodeURIComponent(product.id)}`);
  };

  const handleAuthSuccess = () => {
    setShowAuthModal(false);
    setShowQuoteModal(true);
  };

  const handleCreateAccount = async () => {
    try {
      setAuthError('');
      setIsSubmitting(true);

      // Create user account
      const userCredential = await createUserWithEmailAndPassword(
        auth, 
        formData.email, 
        formData.password
      );
      
      const user = userCredential.user;

      // Save additional user data to Firestore (temporarily disabled for testing)
      try {
        await setDoc(doc(db, 'users', user.uid), {
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          phone: formData.phone,
          businessName: formData.businessName,
          businessAddress: formData.businessAddress,
          city: formData.city,
          state: formData.state,
          zipCode: formData.zipCode,
          businessType: formData.businessType,
          monthlyVolume: formData.monthlyVolume,
          currentProcessor: formData.currentProcessor,
          timeline: formData.timeline,
          additionalNeeds: formData.additionalNeeds,
          createdAt: new Date(),
          userType: 'customer',
          provider: 'email'
        });
      } catch (error) {
        logger.log('Firestore not available, continuing with authentication only');
      }

      handleAuthSuccess();

    } catch (error: any) {
      setAuthError(error.message);
      setIsSubmitting(false);
    }
  };

  const handleSignIn = async () => {
    try {
      setAuthError('');
      setIsSubmitting(true);

      await signInWithEmailAndPassword(auth, formData.email, formData.password);
      handleAuthSuccess();

    } catch (error: any) {
      setAuthError(error.message);
      setIsSubmitting(false);
    }
  };

  const handleGoogleSignIn = async () => {
    try {
      setAuthError('');
      setIsSubmitting(true);

      const result = await signInWithPopup(auth, googleProvider);
      const user = result.user;

      // Save user data to Firestore if it's a new user (temporarily disabled for testing)
      try {
        const userRef = doc(db, 'users', user.uid);
        await setDoc(userRef, {
          firstName: user.displayName?.split(' ')[0] || '',
          lastName: user.displayName?.split(' ').slice(1).join(' ') || '',
          email: user.email,
          phone: user.phoneNumber || '',
          createdAt: new Date(),
          userType: 'customer',
          provider: 'google'
        }, { merge: true });
      } catch (error) {
        logger.log('Firestore not available, continuing with authentication only');
      }

      handleAuthSuccess();

    } catch (error: any) {
      setAuthError(error.message);
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (hasAccount === false) {
      // Create new account
      await handleCreateAccount();
    } else if (hasAccount === true) {
      // Sign in existing user
      await handleSignIn();
    }
  };

  const handleQuoteSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Save quote request and update user profile
      try {
        // Save quote request
        await addDoc(collection(db, 'quoteRequests'), {
          userId: currentUser?.uid,
          productId: selectedProduct?.id,
          productName: selectedProduct?.name,
          businessName: formData.businessName,
          businessType: formData.businessType,
          monthlyVolume: formData.monthlyVolume,
          timeline: formData.timeline,
          additionalNeeds: formData.additionalNeeds,
          status: 'pending',
          createdAt: new Date()
        });

        // Update user profile with business information
        await setDoc(doc(db, 'users', currentUser!.uid), {
          businessName: formData.businessName,
          businessAddress: formData.businessAddress,
          city: formData.city,
          state: formData.state,
          zipCode: formData.zipCode,
          businessType: formData.businessType,
          monthlyVolume: formData.monthlyVolume,
          currentProcessor: formData.currentProcessor,
          timeline: formData.timeline,
          additionalNeeds: formData.additionalNeeds,
          updatedAt: new Date()
        }, { merge: true });

        logger.log('Quote request and user data saved successfully');
      } catch (error) {
        logger.log('Firestore not available, quote request not saved to database');
      }

      setIsSubmitting(false);
      setIsSubmitted(true);
      
      // Reset form after 5 seconds
      setTimeout(() => {
        setShowQuoteModal(false);
        setHasAccount(null);
        setIsSubmitted(false);
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          password: '',
          confirmPassword: '',
          businessName: '',
          businessAddress: '',
          city: '',
          state: '',
          zipCode: '',
          phone: '',
          businessType: '',
          monthlyVolume: '',
          currentProcessor: '',
          timeline: '',
          additionalNeeds: ''
        });
      }, 5000);

    } catch (error: any) {
      setAuthError(error.message);
      setIsSubmitting(false);
    }
  };


  const filteredProducts = (selectedCategory === 'all' 
    ? products 
    : products.filter(product => {
        const productCategory = product.category || 'pos-systems';
        return productCategory === selectedCategory;
      })
  ).sort((a, b) => {
    // Sort: VL products first, then VP products, then others
    const aPrefix = a.name.toUpperCase().startsWith('VL') ? 0 : a.name.toUpperCase().startsWith('VP') ? 1 : 2;
    const bPrefix = b.name.toUpperCase().startsWith('VL') ? 0 : b.name.toUpperCase().startsWith('VP') ? 1 : 2;
    
    if (aPrefix !== bPrefix) {
      return aPrefix - bPrefix;
    }
    
    // If same prefix, sort alphabetically
    return a.name.localeCompare(b.name);
  });

  return (
    <div className="space-y-6 sm:space-y-8 md:space-y-12 pt-4 sm:pt-6 md:pt-8 lg:pt-12 px-3 sm:px-4">
      {/* Header + Category Filter */}
      <ScrollReveal>
      <div className="text-center">
        <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-2 sm:mb-3 md:mb-4">Our Products</h1>
        <p className="text-sm sm:text-base md:text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto px-2">
          Professional payment processing solutions designed to grow with your business
        </p>
      </div>

      <div className="flex flex-wrap justify-center gap-2 sm:gap-3 md:gap-4 px-2">
        {categories.map((category) => (
          <button
            key={category.id}
            onClick={() => setSelectedCategory(category.id)}
            className={`px-3 sm:px-4 md:px-6 py-2 sm:py-2.5 md:py-3 rounded-lg text-xs sm:text-sm md:text-base font-medium transition-colors ${
              selectedCategory === category.id
                ? 'bg-gray-800 text-white'
                : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
            }`}
          >
            {category.name}
          </button>
        ))}
        <button
          onClick={() => setShowCompareModal(true)}
          className="px-3 sm:px-4 md:px-6 py-2 sm:py-2.5 md:py-3 rounded-lg text-xs sm:text-sm md:text-base font-medium transition-colors bg-white text-gray-700 border border-gray-300 hover:bg-gray-50 flex items-center gap-1.5 sm:gap-2"
        >
          <GitCompare className="h-4 w-4 sm:h-5 sm:w-5" />
          <span className="hidden sm:inline">Compare</span>
        </button>
      </div>
      </ScrollReveal>

      {/* Products Grid */}
      {isLoadingProducts && products.length === 0 ? (
        <div className="flex items-center justify-center py-8 sm:py-12">
          <div className="animate-spin rounded-full h-8 w-8 sm:h-12 sm:w-12 border-b-2 border-gray-600"></div>
          <span className="ml-2 sm:ml-3 text-sm sm:text-base text-gray-600">Loading products...</span>
        </div>
      ) : products.length === 0 ? (
        <div className="text-center py-8 sm:py-12">
          <p className="text-sm sm:text-base text-gray-600">No products found.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 md:gap-8">
          {filteredProducts.map((product, index) => (
          <ScrollReveal key={product.id} scale>
          <div className="bg-white rounded-lg shadow-sm sm:shadow-lg border border-gray-200 overflow-hidden hover:shadow-xl transition-shadow flex flex-col">
            {/* Product Image */}
            <div className="h-40 sm:h-48 bg-gradient-to-br from-gray-50 to-gray-50 flex items-center justify-center">
              {product.image.startsWith('data:image/') ? (
                <img 
                  src={product.image} 
                  alt={product.name}
                  className="max-h-full max-w-full object-contain"
                  loading="lazy"
                />
              ) : product.image.startsWith('/') ? (
                <img 
                  src={product.image} 
                  alt={product.name}
                  className="max-h-full max-w-full object-contain"
                  loading="lazy"
                />
              ) : (
                <div className="text-6xl">{product.image}</div>
              )}
            </div>

            {/* Product Info */}
            <div className="p-4 sm:p-5 md:p-6 flex flex-col flex-grow">
              <div className="mb-2">
                <h3 className="text-lg sm:text-xl font-semibold text-gray-900">{product.name}</h3>
              </div>

              {/* Price */}
              <div className="mb-3 sm:mb-4">
                <div className="flex items-center gap-2">
                  <span className="text-xl sm:text-2xl font-bold text-gray-900">{product.price}</span>
                  {product.originalPrice && (
                    <span className="text-base sm:text-lg text-gray-500 line-through">{product.originalPrice}</span>
                  )}
        </div>
      </div>

              {/* Supported Industries */}
              <div className="mb-4 sm:mb-6 flex-grow">
                <h4 className="text-sm sm:text-base font-medium text-gray-900 mb-1.5 sm:mb-2">Supported Industries:</h4>
                <div className="flex flex-wrap gap-1.5 sm:gap-2">
                  {Array.isArray(product.industry) ? (
                    product.industry.map((industry, index) => (
                      <span key={index} className="inline-flex items-center px-2 sm:px-2.5 py-0.5 rounded-full text-[10px] sm:text-xs font-medium bg-gray-100 text-gray-800">
                        {industry}
                      </span>
                    ))
                  ) : product.industry ? (
                    <span className="inline-flex items-center px-2 sm:px-2.5 py-0.5 rounded-full text-[10px] sm:text-xs font-medium bg-gray-100 text-gray-800">
                      {product.industry}
                    </span>
                  ) : (
                    <span className="text-xs sm:text-sm text-gray-500">No industries specified</span>
                  )}
                </div>
    </div>

              {/* CTA Buttons - Fixed at bottom */}
              <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 items-stretch mt-auto">
        <button
                  onClick={() => handleGetQuote(product)}
                  className="flex-1 bg-gradient-to-r from-gray-800 to-gray-900 text-white px-3 sm:px-4 py-2 sm:py-2.5 rounded-lg text-sm sm:text-base font-medium hover:from-gray-900 hover:to-black transition-all duration-300 flex items-center justify-center min-h-[44px]"
                >
                  Get a Quote
                </button>
                <button 
                  onClick={() => {
                    setSelectedLearnMoreProduct(product);
                    setShowLearnMoreModal(true);
                  }}
                  className="px-3 sm:px-4 py-2 sm:py-2.5 border border-gray-300 text-gray-700 rounded-lg text-sm sm:text-base font-medium hover:bg-gray-50 transition-colors flex items-center justify-center min-h-[44px]"
                >
                  Learn More
        </button>
      </div>
            </div>
          </div>
          </ScrollReveal>
        ))}
      </div>
      )}

      {/* Why Choose Us */}
      <ScrollReveal>
      <div className="bg-gradient-to-br from-gray-50 to-gray-50 py-8 sm:py-12 md:py-16 rounded-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-6 sm:mb-8 md:mb-12">
            <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 mb-2 sm:mb-3 md:mb-4">Why Choose Our Products?</h2>
            <p className="text-sm sm:text-base md:text-lg lg:text-xl text-gray-600">We provide more than just hardware</p>
          </div>

          {/* Features Section - Four Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5 md:gap-6 mb-6 sm:mb-8 md:mb-12">
            {/* Bank-Level Security */}
            <ScrollReveal scale>
            <div className="bg-white rounded-xl p-4 sm:p-5 md:p-6 shadow-sm sm:shadow-lg border border-gray-100 hover:shadow-xl transition-shadow">
              <div className="flex justify-center mb-3 sm:mb-4">
                <Shield className="h-8 w-8 sm:h-10 sm:w-10 md:h-12 md:w-12 text-gray-900" />
              </div>
              <h3 className="text-base sm:text-lg md:text-xl font-semibold text-gray-900 mb-1.5 sm:mb-2 text-center">Bank-Level Security</h3>
              <p className="text-xs sm:text-sm md:text-base text-gray-600 text-center">PCI DSS compliant with end-to-end encryption</p>
            </div>
            </ScrollReveal>

            {/* Same-Day Deposits */}
            <ScrollReveal scale>
            <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-100 hover:shadow-xl transition-shadow">
              <div className="flex justify-center mb-4">
                <Clock className="h-12 w-12 text-green-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2 text-center">Same-Day Deposits</h3>
              <p className="text-gray-600 text-center">Get your money faster with next-day funding</p>
            </div>
            </ScrollReveal>

            {/* Always Connected */}
            <ScrollReveal scale>
            <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-100 hover:shadow-xl transition-shadow">
              <div className="flex justify-center mb-4">
                <Wifi className="h-12 w-12 text-gray-900" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2 text-center">Always Connected</h3>
              <p className="text-gray-600 text-center">WiFi, 5G, and offline modes for reliability</p>
            </div>
            </ScrollReveal>

            {/* Competitive Rates */}
            <ScrollReveal scale>
            <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-100 hover:shadow-xl transition-shadow">
              <div className="flex justify-center mb-4">
                <DollarSign className="h-12 w-12 text-yellow-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2 text-center">Competitive Rates</h3>
              <p className="text-gray-600 text-center">Transparent pricing with no hidden fees</p>
            </div>
            </ScrollReveal>
          </div>

          {/* Three Cards Section */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <ScrollReveal scale>
            <div className="text-center bg-white rounded-xl p-8 shadow-lg border border-gray-100">
              <div className="bg-gradient-to-br from-gray-500 to-gray-800 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <CreditCard className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Complete Solutions</h3>
              <p className="text-gray-600">Hardware, software, and support all in one package</p>
            </div>
            </ScrollReveal>
            <ScrollReveal scale>
            <div className="text-center bg-white rounded-xl p-8 shadow-lg border border-gray-100">
              <div className="bg-gradient-to-br from-gray-500 to-gray-800 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Smartphone className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Easy Setup</h3>
              <p className="text-gray-600">Get up and running in minutes with our simple setup process</p>
            </div>
            </ScrollReveal>
            <ScrollReveal scale>
            <div className="text-center bg-white rounded-xl p-8 shadow-lg border border-gray-100">
              <div className="bg-gradient-to-br from-gray-500 to-gray-800 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Monitor className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">24/7 Support</h3>
              <p className="text-gray-600">Round-the-clock technical support when you need it</p>
            </div>
            </ScrollReveal>
          </div>
        </div>
      </div>
      </ScrollReveal>

      {/* CTA Section */}
      <ScrollReveal>
      <div className="bg-gray-900 text-white py-8 sm:py-12 md:py-16 rounded-lg">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold mb-2 sm:mb-3 md:mb-4">Need Help Choosing?</h2>
          <p className="text-sm sm:text-base md:text-lg lg:text-xl mb-4 sm:mb-6 md:mb-8 text-gray-100">
            Our experts can help you find the perfect payment solution for your business
          </p>
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
            <a 
              href="/contact" 
              className="bg-white text-gray-700 px-4 sm:px-6 md:px-8 py-2.5 sm:py-3 md:py-4 rounded-lg font-semibold text-sm sm:text-base md:text-lg hover:bg-gray-50 transition-colors inline-flex items-center justify-center gap-2 shadow-lg"
            >
              Get Free Consultation
              <ArrowRight className="h-4 w-4 sm:h-5 sm:w-5" />
            </a>
            <a 
              href="tel:+12018460591" 
              className="border-2 border-white text-white px-4 sm:px-6 md:px-8 py-2.5 sm:py-3 md:py-4 rounded-lg font-semibold text-sm sm:text-base md:text-lg hover:bg-white hover:text-gray-700 transition-colors inline-flex items-center justify-center gap-2"
            >
              <Phone className="h-4 w-4 sm:h-5 sm:w-5" />
              <span className="hidden sm:inline">Call </span>(201) 846-0591
            </a>
          </div>
        </div>
      </div>
      </ScrollReveal>

              {/* Authentication Modal */}
              {showAuthModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[60] !mt-0">
                  <div className="bg-white rounded-2xl max-w-md w-full shadow-2xl">
                    <div className="p-8">
                      <div className="text-center mb-8">
                        <div className="mx-auto w-16 h-16 bg-gradient-to-r from-gray-800 to-gray-900 rounded-full flex items-center justify-center mb-4">
                          <User className="h-8 w-8 text-white" />
                        </div>
                        <h2 className="text-2xl font-bold text-gray-900 mb-2">
                          Welcome to Simpl Processing
                        </h2>
                        <p className="text-gray-600">
                          Sign in to get your personalized quote for {selectedProduct?.name}
                        </p>
                      </div>

                      {/* Auth Error Display */}
                      {authError && (
                        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                          <p className="text-red-800 text-sm">{authError}</p>
                        </div>
                      )}

                      {/* Social Sign In Buttons */}
                      <div className="space-y-4 mb-6">
                        <button
                          onClick={handleGoogleSignIn}
                          disabled={isSubmitting}
                          className="w-full flex items-center justify-center px-4 py-3 border border-gray-300 rounded-lg shadow-sm bg-white text-gray-700 font-medium hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
                            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                          </svg>
                          Continue with Google
                        </button>

                        <button
                          disabled
                          className="w-full flex items-center justify-center px-4 py-3 border border-gray-300 rounded-lg shadow-sm bg-white text-gray-400 font-medium cursor-not-allowed"
                        >
                          <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M12.152 6.896c-.948 0-2.415-1.078-3.96-1.04-2.04.027-3.91 1.183-4.961 3.014-2.117 3.675-.546 9.103 1.519 12.09 1.013 1.454 2.208 3.09 3.792 3.039 1.52-.065 2.09-.987 3.935-.987 1.831 0 2.35.987 3.96.948 1.637-.026 2.676-1.48 3.676-2.948 1.156-1.688 1.636-3.325 1.662-3.415-.039-.013-3.182-1.221-3.22-4.857-.026-3.04 2.48-4.494 2.597-4.559-1.429-2.09-3.623-2.324-4.39-2.376-2-.156-3.675 1.09-4.61 1.09zM15.53 3.83c.843-1.012 1.4-2.427 1.245-3.83-1.207.052-2.662.805-3.532 1.818-.78.896-1.454 2.338-1.273 3.714 1.338.104 2.715-.688 3.559-1.701"/>
                          </svg>
                          Continue with Apple
                          <span className="ml-2 text-xs bg-gray-200 px-2 py-1 rounded">Coming Soon</span>
                        </button>
                      </div>

                      {/* Divider */}
                      <div className="relative mb-6">
                        <div className="absolute inset-0 flex items-center">
                          <div className="w-full border-t border-gray-300" />
                        </div>
                        <div className="relative flex justify-center text-sm">
                          <span className="px-2 bg-white text-gray-500">Or continue with email</span>
                        </div>
                      </div>

                      {/* Account Question */}
                      {hasAccount === null && (
                        <div className="text-center">
                          <h3 className="text-lg font-semibold text-gray-900 mb-4">Do you have an account?</h3>
                          <div className="flex gap-4">
                            <button
                              type="button"
                              onClick={() => {
                                setHasAccount(true);
                                setIsLoginMode(true);
                              }}
                              className="flex-1 bg-gradient-to-r from-gray-800 to-gray-900 text-white px-6 py-3 rounded-lg font-semibold hover:from-gray-900 hover:to-black transition-all duration-300"
                            >
                              Sign In
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                setHasAccount(false);
                                setIsLoginMode(false);
                              }}
                              className="flex-1 border-2 border-gray-600 text-gray-800 px-6 py-3 rounded-lg font-semibold hover:bg-gray-50 transition-colors"
                            >
                              Sign Up
                            </button>
                          </div>
                        </div>
                      )}

                      {/* Email/Password Form */}
                      {hasAccount !== null && (
                        <form onSubmit={handleSubmit} className="space-y-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Email Address *
                            </label>
                            <input
                              type="email"
                              name="email"
                              value={formData.email}
                              onChange={handleInputChange}
                              required
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-gray-500"
                              placeholder="john@business.com"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Password *
                            </label>
                            <input
                              type="password"
                              name="password"
                              value={formData.password}
                              onChange={handleInputChange}
                              required
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-gray-500"
                              placeholder={isLoginMode ? "Enter your password" : "Create a password"}
                            />
                          </div>
                          {!isLoginMode && (
                            <>
                              <div className="grid grid-cols-2 gap-4">
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">
                                    First Name *
                                  </label>
                                  <input
                                    type="text"
                                    name="firstName"
                                    value={formData.firstName}
                                    onChange={handleInputChange}
                                    required
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-gray-500"
                                    placeholder="John"
                                  />
                                </div>
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Last Name *
                                  </label>
                                  <input
                                    type="text"
                                    name="lastName"
                                    value={formData.lastName}
                                    onChange={handleInputChange}
                                    required
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-gray-500"
                                    placeholder="Smith"
                                  />
                                </div>
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                  Phone Number *
                                </label>
                                <input
                                  type="tel"
                                  name="phone"
                                  value={formData.phone}
                                  onChange={handleInputChange}
                                  required
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-gray-500"
                                  placeholder="(555) 123-4567"
                                />
                              </div>
                            </>
                          )}
                          <div className="flex gap-4 pt-4">
                            <button
                              type="button"
                              onClick={() => setShowAuthModal(false)}
                              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors"
                            >
                              Cancel
                            </button>
                            <button
                              type="submit"
                              disabled={isSubmitting}
                              className="flex-1 bg-gradient-to-r from-gray-800 to-gray-900 text-white px-4 py-2 rounded-lg font-semibold hover:from-gray-900 hover:to-black transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                            >
                              {isSubmitting ? (
                                <>
                                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                  {isLoginMode ? 'Signing In...' : 'Creating Account...'}
                                </>
                              ) : (
                                isLoginMode ? 'Sign In' : 'Create Account'
                              )}
                            </button>
                          </div>
                          {isLoginMode && (
                            <div className="text-center">
          <button
                                type="button"
                                onClick={() => {
                                  setIsLoginMode(false);
                                  setHasAccount(false);
                                }}
                                className="text-gray-800 hover:text-gray-700 text-sm font-medium"
                              >
                                Don't have an account? Create one
          </button>
                            </div>
                          )}
                        </form>
                      )}
                    </div>
                  </div>
        </div>
      )}

              {/* Quote Modal */}
              {showQuoteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[60] !mt-0">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900">
                  Get Quote for {selectedProduct?.name}
                </h2>
                <button
                  onClick={() => setShowQuoteModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              {isSubmitted ? (
                <div className="text-center py-8">
                  <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Quote Request Submitted!</h3>
                  <p className="text-gray-600 mb-4">
                    We've received your information and will contact you within 24 hours with a personalized quote for {selectedProduct?.name}.
                  </p>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p className="text-sm text-gray-800">
                      <strong>Next Steps:</strong> Our team will review your requirements and prepare a customized solution for your business.
                    </p>
                  </div>
                </div>
              ) : (
                <form onSubmit={handleQuoteSubmit} className="space-y-6">
                  {/* Auth Error Display */}
                  {authError && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                      <p className="text-red-800 text-sm">{authError}</p>
                    </div>
                  )}

                  {/* Business Information */}
                      <div>
                        <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                          <Building className="h-5 w-5 mr-2" />
                          Business Information
                        </h3>
                        <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                              Business Name *
                  </label>
                  <input
                    type="text"
                              name="businessName"
                              value={formData.businessName}
                              onChange={handleInputChange}
                              required
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-gray-500"
                              placeholder="Your Business LLC"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                              Business Address *
                            </label>
                            <input
                              type="text"
                              name="businessAddress"
                              value={formData.businessAddress}
                              onChange={handleInputChange}
                              required
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-gray-500"
                              placeholder="123 Main Street"
                            />
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                City *
                  </label>
                              <input
                                type="text"
                                name="city"
                                value={formData.city}
                                onChange={handleInputChange}
                                required
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-gray-500"
                                placeholder="New York"
                  />
                </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                State *
                    </label>
                    <input
                                type="text"
                                name="state"
                                value={formData.state}
                                onChange={handleInputChange}
                                required
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-gray-500"
                                placeholder="NY"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                ZIP Code *
                    </label>
                    <input
                                type="text"
                                name="zipCode"
                                value={formData.zipCode}
                                onChange={handleInputChange}
                                required
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-gray-500"
                                placeholder="10001"
                    />
                  </div>
                </div>
                        </div>
                      </div>

                      {/* Business Details */}
                      <div>
                        <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                          <CreditCard className="h-5 w-5 mr-2" />
                          Business Details
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Type of Business *
                            </label>
                            <select
                              name="businessType"
                              value={formData.businessType}
                              onChange={handleInputChange}
                              required
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-gray-500"
                            >
                              <option value="">Select business type</option>
                              {businessTypes.map(type => (
                                <option key={type} value={type}>{type}</option>
                              ))}
                            </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                              Monthly Processing Volume *
                  </label>
                            <select
                              name="monthlyVolume"
                              value={formData.monthlyVolume}
                              onChange={handleInputChange}
                              required
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-gray-500"
                            >
                              <option value="">Select volume</option>
                              <option value="0-1000">$0 - $1,000</option>
                              <option value="1000-5000">$1,000 - $5,000</option>
                              <option value="5000-10000">$5,000 - $10,000</option>
                              <option value="10000-25000">$10,000 - $25,000</option>
                              <option value="25000-50000">$25,000 - $50,000</option>
                              <option value="50000+">$50,000+</option>
                  </select>
                </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Current Payment Processor
                            </label>
                            <input
                              type="text"
                              name="currentProcessor"
                              value={formData.currentProcessor}
                              onChange={handleInputChange}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-gray-500"
                              placeholder="Square, Stripe, etc."
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Timeline *
                            </label>
                            <select
                              name="timeline"
                              value={formData.timeline}
                              onChange={handleInputChange}
                              required
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-gray-500"
                            >
                              <option value="">Select timeline</option>
                              {timelines.map(timeline => (
                                <option key={timeline} value={timeline}>{timeline}</option>
                              ))}
                            </select>
                          </div>
                        </div>
                        <div className="mt-4">
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Additional Requirements
                          </label>
                          <textarea
                            name="additionalNeeds"
                            value={formData.additionalNeeds}
                            onChange={handleInputChange}
                            rows={3}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-gray-500"
                            placeholder="Tell us about any specific features or requirements..."
                          />
                        </div>
                      </div>

                      {/* Submit Button */}
                      <div className="flex gap-4 pt-6">
                  <button
                    type="button"
                          onClick={() => setShowQuoteModal(false)}
                          className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                        <button
                          type="submit"
                          disabled={isSubmitting}
                          className="flex-1 bg-gradient-to-r from-gray-800 to-gray-900 text-white px-6 py-3 rounded-lg font-semibold hover:from-gray-900 hover:to-black transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                        >
                          {isSubmitting ? (
                            <>
                              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                              Submitting...
                            </>
                          ) : (
                            <>
                              <Mail className="h-5 w-5" />
                              Get My Quote
                            </>
                          )}
                  </button>
                </div>
              </form>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Learn More Modal */}
      {showLearnMoreModal && selectedLearnMoreProduct && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[60] !mt-0">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900">
                  {selectedLearnMoreProduct.name}
                </h2>
                <button
                  onClick={() => {
                    setShowLearnMoreModal(false);
                    setSelectedLearnMoreProduct(null);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              {/* Product Image */}
              <div className="mb-6">
                <div className="h-64 bg-gradient-to-br from-gray-50 to-gray-50 flex items-center justify-center rounded-lg">
                  {selectedLearnMoreProduct.image.startsWith('data:image/') ? (
                    <img 
                      src={selectedLearnMoreProduct.image} 
                      alt={selectedLearnMoreProduct.name}
                      className="max-h-full max-w-full object-contain"
                    />
                  ) : selectedLearnMoreProduct.image.startsWith('/') ? (
                    <img 
                      src={selectedLearnMoreProduct.image} 
                      alt={selectedLearnMoreProduct.name}
                      className="max-h-full max-w-full object-contain"
                    />
                  ) : (
                    <div className="text-6xl">{selectedLearnMoreProduct.image}</div>
                  )}
                </div>
              </div>

              {/* Product Details */}
              <div className="space-y-6">
                {/* Price */}
                <div className="flex items-center gap-2">
                  <span className="text-3xl font-bold text-gray-900">{selectedLearnMoreProduct.price}</span>
                  {selectedLearnMoreProduct.originalPrice && (
                    <span className="text-xl text-gray-500 line-through">{selectedLearnMoreProduct.originalPrice}</span>
                  )}
                </div>

                {/* Supported Industries */}
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Supported Industries:</h4>
                  <div className="flex flex-wrap gap-2">
                    {Array.isArray(selectedLearnMoreProduct.industry) ? (
                      selectedLearnMoreProduct.industry.map((industry, index) => (
                        <span key={index} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                          {industry}
                        </span>
                      ))
                    ) : selectedLearnMoreProduct.industry ? (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                        {selectedLearnMoreProduct.industry}
                      </span>
                    ) : (
                      <span className="text-sm text-gray-500">No industries specified</span>
                    )}
                  </div>
                </div>

                {/* Description */}
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Description:</h4>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    {selectedLearnMoreProduct.description || 'No description available'}
                  </p>
                </div>

                {/* Warranty */}
                {selectedLearnMoreProduct.warranty && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-5 w-5 text-green-600" />
                      <span className="text-sm font-medium text-green-800">1 Year Warranty Included</span>
                    </div>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex gap-3 pt-4">
                  <button
                    onClick={() => {
                      setShowLearnMoreModal(false);
                      setSelectedLearnMoreProduct(null);
                      handleGetQuote(selectedLearnMoreProduct);
                    }}
                    className="flex-1 bg-gradient-to-r from-gray-800 to-gray-900 text-white px-6 py-3 rounded-lg font-semibold hover:from-gray-900 hover:to-black transition-all duration-300"
                  >
                    Get a Quote
                  </button>
                  <button
                    onClick={() => {
                      setShowLearnMoreModal(false);
                      setSelectedLearnMoreProduct(null);
                    }}
                    className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Compare Modal */}
      {showCompareModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[60] !mt-0">
          <div className="bg-white rounded-lg max-w-6xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Compare Products</h2>
                <button
                  onClick={() => {
                    setShowCompareModal(false);
                    setCompareProducts([]);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              {compareProducts.length === 2 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {compareProducts.map((product) => (
                    <div key={product.id} className="border border-gray-200 rounded-lg p-4">
                      <div className="text-center mb-4">
                        <div className="h-32 bg-gradient-to-br from-gray-50 to-gray-50 flex items-center justify-center mb-4 rounded-lg">
                          {product.image.startsWith('data:image/') ? (
                            <img 
                              src={product.image} 
                              alt={product.name}
                              className="max-h-full max-w-full object-contain"
                            />
                          ) : product.image.startsWith('/') ? (
                            <img 
                              src={product.image} 
                              alt={product.name}
                              className="max-h-full max-w-full object-contain"
                            />
                          ) : (
                            <div className="text-4xl">{product.image}</div>
                          )}
                        </div>
                        <h3 className="text-xl font-semibold text-gray-900 mb-2">{product.name}</h3>
                        <div className="text-2xl font-bold text-gray-900 mb-4">{product.price}</div>
                      </div>

                      <div className="space-y-4">
                        <div>
                          <h4 className="font-medium text-gray-900 mb-2">Category</h4>
                          <p className="text-sm text-gray-600">{product.category || 'N/A'}</p>
                        </div>

                        <div>
                          <h4 className="font-medium text-gray-900 mb-2">Supported Industries</h4>
                          <div className="flex flex-wrap gap-2">
                            {Array.isArray(product.industry) ? (
                              product.industry.map((industry, index) => (
                                <span key={index} className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                  {industry}
                                </span>
                              ))
                            ) : product.industry ? (
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                {product.industry}
                              </span>
                            ) : (
                              <span className="text-sm text-gray-500">N/A</span>
                            )}
                          </div>
                        </div>

                        <div>
                          <h4 className="font-medium text-gray-900 mb-2">Description</h4>
                          <p className="text-sm text-gray-600">{product.description || 'No description available'}</p>
                        </div>


                        {product.warranty && (
                          <div>
                            <h4 className="font-medium text-gray-900 mb-2">Warranty</h4>
                            <p className="text-sm text-gray-600">1 Year Warranty Included</p>
                          </div>
                        )}

                        <div>
                          <h4 className="font-medium text-gray-900 mb-2">Action</h4>
                          <button
                            onClick={() => {
                              setShowCompareModal(false);
                              handleGetQuote(product);
                            }}
                            className="w-full bg-gradient-to-r from-gray-800 to-gray-900 text-white px-4 py-2 rounded-lg font-medium hover:from-gray-900 hover:to-black transition-all duration-300"
                          >
                            Get a Quote
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div>
                  <div className="mb-6">
                    <p className="text-sm text-gray-600 mb-4">
                      Select 2 products to compare side by side
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Product 1 {compareProducts[0] && `(${compareProducts[0].name})`}
                        </label>
                        <select
                          value={compareProducts[0]?.id || ''}
                          onChange={(e) => {
                            const selectedProduct = filteredProducts.find(p => p.id === e.target.value);
                            if (selectedProduct) {
                              if (compareProducts.length === 0) {
                                setCompareProducts([selectedProduct]);
                              } else if (compareProducts.length === 1) {
                                setCompareProducts([selectedProduct, compareProducts[0]]);
                              } else {
                                setCompareProducts([selectedProduct, compareProducts[1]]);
                              }
                            } else {
                              setCompareProducts(compareProducts.slice(1));
                            }
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-gray-500"
                        >
                          <option value="">Select a product...</option>
                          {filteredProducts
                            .sort((a, b) => {
                              const aPrefix = a.name.toUpperCase().startsWith('VL') ? 0 : a.name.toUpperCase().startsWith('VP') ? 1 : 2;
                              const bPrefix = b.name.toUpperCase().startsWith('VL') ? 0 : b.name.toUpperCase().startsWith('VP') ? 1 : 2;
                              if (aPrefix !== bPrefix) return aPrefix - bPrefix;
                              return a.name.localeCompare(b.name);
                            })
                            .map((product) => (
                              <option key={product.id} value={product.id}>
                                {product.name} - {product.price}
                              </option>
                            ))}
                        </select>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Product 2 {compareProducts[1] && `(${compareProducts[1].name})`}
                        </label>
                        <select
                          value={compareProducts[1]?.id || ''}
                          onChange={(e) => {
                            const selectedProduct = filteredProducts.find(p => p.id === e.target.value);
                            if (selectedProduct) {
                              if (compareProducts.length === 0) {
                                setCompareProducts([selectedProduct]);
                              } else if (compareProducts.length === 1) {
                                setCompareProducts([compareProducts[0], selectedProduct]);
                              } else {
                                setCompareProducts([compareProducts[0], selectedProduct]);
                              }
                            } else {
                              setCompareProducts(compareProducts.slice(0, 1));
                            }
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-gray-500"
                        >
                          <option value="">Select a product...</option>
                          {filteredProducts
                            .sort((a, b) => {
                              const aPrefix = a.name.toUpperCase().startsWith('VL') ? 0 : a.name.toUpperCase().startsWith('VP') ? 1 : 2;
                              const bPrefix = b.name.toUpperCase().startsWith('VL') ? 0 : b.name.toUpperCase().startsWith('VP') ? 1 : 2;
                              if (aPrefix !== bPrefix) return aPrefix - bPrefix;
                              return a.name.localeCompare(b.name);
                            })
                            .map((product) => (
                              <option key={product.id} value={product.id}>
                                {product.name} - {product.price}
                              </option>
                            ))}
                        </select>
                      </div>
                    </div>
                  </div>
                  
                  {compareProducts.length < 2 && (
                    <div className="text-center py-8">
                      <GitCompare className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                      <p className="text-gray-600">
                        Select {2 - compareProducts.length} more product{2 - compareProducts.length > 1 ? 's' : ''} to compare
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Products;